import type { IndicatorSeries } from "../types.ts";
import { analyzeDirectionalTransmission, type DirectionalTransmissionEvidence, type TransmissionTransform } from "./transmissionEngine.ts";
import type { ExternalShockRegistry } from "./shockIdentification.ts";

export type PitAvailabilityMode = "exact_release_vintage" | "observation_date_proxy" | "latest_revision_only" | "unavailable";
export type PitAvailabilityStatus = "certified" | "proxy" | "blocked";

export interface PitSeriesAudit {
  seriesId: string;
  label: string;
  mode: PitAvailabilityMode;
  status: PitAvailabilityStatus;
  numericObservations: number;
  note: string;
}

export interface PitReplayPoint {
  asOfDate: string;
  samples: number | null;
  verdict: DirectionalTransmissionEvidence["verdict"];
  forwardCorrelation: number | null;
  reverseCorrelation: number | null;
  forwardPeakLagMonths: number | null;
}

export interface PitEdgeReplay {
  id: string;
  label: string;
  status: "ready" | "blocked" | "insufficient";
  leaderAudit: PitSeriesAudit;
  followerAudit: PitSeriesAudit;
  points: PitReplayPoint[];
  note: string;
}

export interface PitReplayAuditBundle {
  generatedAt: string;
  series: PitSeriesAudit[];
  counts: { certified: number; proxy: number; blocked: number };
  housingExactReplayReady: boolean;
  edgeReplays: PitEdgeReplay[];
  externalRegistryStatus: ExternalShockRegistry["status"];
  note: string;
}

function numericCount(series: IndicatorSeries | null | undefined) {
  return series?.observations.filter((obs) => obs.value != null && Number.isFinite(obs.value)).length ?? 0;
}

export function auditSeriesPit(series: IndicatorSeries | null | undefined, options?: { exactReleaseVintage?: boolean }): PitSeriesAudit {
  if (!series || numericCount(series) === 0) return { seriesId: series?.id ?? "unavailable", label: series?.name ?? "unavailable", mode: "unavailable", status: "blocked", numericObservations: 0, note: "숫자관측이 없어 PIT replay를 수행할 수 없습니다." };
  const count = numericCount(series);
  if (options?.exactReleaseVintage) return { seriesId: series.id, label: series.name, mode: "exact_release_vintage", status: "certified", numericObservations: count, note: "관측별 release/vintage availability가 검증된 시계열입니다." };
  if (series.source.frequency === "daily") return { seriesId: series.id, label: series.name, mode: "observation_date_proxy", status: "proxy", numericObservations: count, note: "일별 시장/정책 자료를 관측일 기준으로 자릅니다. revision-free release vintage를 인증한 것은 아니므로 proxy입니다." };
  return { seriesId: series.id, label: series.name, mode: "latest_revision_only", status: "blocked", numericObservations: count, note: "월별/분기 자료의 관측별 release/vintage ledger가 없어 정확한 당시시점 replay를 차단합니다." };
}

function truncateSeries(series: IndicatorSeries, asOfDate: string): IndicatorSeries {
  return { ...series, observations: series.observations.filter((obs) => obs.date <= asOfDate) };
}

function yearlyAsOfDates(leader: IndicatorSeries, follower: IndicatorSeries) {
  const dates = [...leader.observations, ...follower.observations]
    .filter((obs) => obs.value != null && /^\d{4}-\d{2}-\d{2}$/.test(obs.date))
    .map((obs) => obs.date)
    .sort();
  if (!dates.length) return [];
  const startYear = Number(dates[0].slice(0, 4));
  const endYear = Number(dates.at(-1)!.slice(0, 4));
  const out: string[] = [];
  for (let year = startYear + 3; year <= endYear; year++) out.push(`${year}-12-31`);
  const latest = dates.at(-1)!;
  if (!out.length || latest > out.at(-1)!) out.push(latest);
  return out;
}

export function buildPitEdgeReplay(args: {
  id: string;
  label: string;
  leader: IndicatorSeries;
  follower: IndicatorSeries;
  leaderTransform?: TransmissionTransform;
  followerTransform?: TransmissionTransform;
  leaderAudit?: PitSeriesAudit;
  followerAudit?: PitSeriesAudit;
}): PitEdgeReplay {
  const leaderAudit = args.leaderAudit ?? auditSeriesPit(args.leader);
  const followerAudit = args.followerAudit ?? auditSeriesPit(args.follower);
  if (leaderAudit.status === "blocked" || followerAudit.status === "blocked") return {
    id: args.id, label: args.label, status: "blocked", leaderAudit, followerAudit, points: [],
    note: "두 시계열 모두 PIT certified/proxy여야 as-of 방향성 replay를 허용합니다."
  };
  const points = yearlyAsOfDates(args.leader, args.follower).flatMap((asOfDate) => {
    const evidence = analyzeDirectionalTransmission({
      id: `${args.id}-${asOfDate}`,
      label: args.label,
      leader: truncateSeries(args.leader, asOfDate),
      follower: truncateSeries(args.follower, asOfDate),
      leaderTransform: args.leaderTransform ?? "first_difference",
      followerTransform: args.followerTransform ?? "first_difference",
      maxLagMonths: 12,
      minimumSamples: 12,
      note: `PIT as-of ${asOfDate}`
    });
    const samples = evidence.forward.minimumSamples;
    if (evidence.status === "insufficient") return [];
    return [{
      asOfDate,
      samples,
      verdict: evidence.verdict,
      forwardCorrelation: evidence.forward.correlation,
      reverseCorrelation: evidence.reverse.correlation,
      forwardPeakLagMonths: evidence.timing.peakLagMonths
    } satisfies PitReplayPoint];
  });
  return {
    id: args.id, label: args.label,
    status: points.length >= 2 ? "ready" : "insufficient",
    leaderAudit, followerAudit, points,
    note: "as-of 시점에 관측일 기준으로 이용 가능했다고 간주할 수 있는 데이터만 잘라 동일한 양방향 방향성 scan을 재실행합니다. proxy는 exact release-vintage backtest가 아닙니다."
  };
}

export function buildPitReplayAudit(args: {
  series: Array<{ series: IndicatorSeries | null | undefined; exactReleaseVintage?: boolean }>;
  housingSeries?: IndicatorSeries[];
  edgeReplays?: PitEdgeReplay[];
  externalRegistry: ExternalShockRegistry;
}): PitReplayAuditBundle {
  const audited = args.series.map((item) => auditSeriesPit(item.series, { exactReleaseVintage: item.exactReleaseVintage }));
  const housingAudits = (args.housingSeries ?? []).map((series) => auditSeriesPit(series));
  const all = [...audited, ...housingAudits].filter((item, index, arr) => arr.findIndex((other) => other.seriesId === item.seriesId) === index);
  const counts = {
    certified: all.filter((item) => item.status === "certified").length,
    proxy: all.filter((item) => item.status === "proxy").length,
    blocked: all.filter((item) => item.status === "blocked").length
  };
  return {
    generatedAt: new Date().toISOString(),
    series: all,
    counts,
    housingExactReplayReady: housingAudits.length > 0 && housingAudits.every((item) => item.status === "certified"),
    edgeReplays: args.edgeReplays ?? [],
    externalRegistryStatus: args.externalRegistry.status,
    note: "PIT-safe shock와 PIT-safe outcome은 별개 조건입니다. shock가 externally identified여도 outcome의 당시 release/vintage가 없으면 과거 실시간 검증은 blocked입니다."
  };
}
